import React, { useState } from 'react'

/**
 * InboundCallPopupDrawer
 * Slide-in popup drawer for incoming ring & active call notes.
 * Features customer context cards, quick task/job creation overlays,
 * call notes text area, and a "Minimize to Header Bar" button.
 */
export default function InboundCallPopupDrawer({
  isOpen = false,
  callState = 'RINGING', // 'RINGING' | 'CONNECTED'
  direction = 'inbound', // 'inbound' | 'outbound'
  callerName = 'Customer',
  callerNumber = '',
  projectName = '',
  customerName = '',
  contactPerson = '',
  isKnownCaller = true,
  lastInteraction = 'Last call on July 28, 2026. ERP Ticket #4082 resolved.',
  openTicketsCount = 0,
  openTasksCount = 0,
  isMuted = false,
  isOnHold = false,
  callerInfo = null,
  onToggleMute = () => {},
  onToggleHold = () => {},
  onAnswer = null,
  onAnswerCall = null,
  onDeclineCall = null,
  onReject = null,
  onHangup = null,
  onMinimizeToHeader = null,
  onMinimizeToDock = null,
  notes = '',
  onSaveNotes = (notes) => {},
  onQuickCreateTask = () => {},
  onQuickCreateJob = () => {},
  onScheduleCallback = () => {},
}) {
  const [notesText, setNotesText] = useState(notes || '')

  React.useEffect(() => {
    if (notes !== undefined && notes !== null) {
      setNotesText(notes)
    }
  }, [notes])

  if (!isOpen) return null

  // Normalize prop sources (supports both top-level props and callerInfo object)
  const effectiveDirection = (callerInfo?.direction || direction || 'inbound').toLowerCase()
  const effectiveNumber = callerInfo?.from || callerInfo?.called_number || callerInfo?.caller_number || callerNumber || 'Unknown Number'
  const effectiveContactName = callerInfo?.contact_name || callerInfo?.contact_person || callerInfo?.customer_name || customerName || 'Customer / Lead'
  const effectiveProjectName = callerInfo?.project_name || projectName || ''
  const effectiveContactPerson = callerInfo?.contact_name || callerInfo?.contact_person || contactPerson || ''

  // Normalize handler callbacks
  const handleAnswer = onAnswerCall || onAnswer || (() => {})
  const handleDecline = onDeclineCall || onReject || onHangup || (() => {})
  const handleMinimize = onMinimizeToHeader || onMinimizeToDock || (() => {})

  // Determine Title & Banner Theme based on callState and direction
  let titleText = 'Call Active'
  let bannerClass = 'bg-gradient-to-r from-primary to-primary-container text-on-primary'
  let iconName = 'call_log'

  if (callState === 'RINGING') {
    if (effectiveDirection === 'outbound') {
      titleText = 'Calling...'
      bannerClass = 'bg-gradient-to-r from-blue-600 to-indigo-700 text-white'
      iconName = 'phone_forwarded'
    } else {
      titleText = 'Incoming Call...'
      bannerClass = 'bg-gradient-to-r from-emerald-600 to-green-700 text-white animate-pulse'
      iconName = 'ring_volume'
    }
  } else if (callState === 'CONNECTING') {
    titleText = 'Connecting...'
    bannerClass = 'bg-gradient-to-r from-blue-600 to-indigo-700 text-white'
    iconName = 'hourglass_top'
  } else if (callState === 'MISSED') {
    titleText = 'Call Missed / Hunt Forwarded'
    bannerClass = 'bg-gradient-to-r from-amber-600 to-rose-700 text-white'
    iconName = 'phone_missed'
  } else {
    titleText = effectiveDirection === 'outbound' ? 'Outbound Call Active' : 'Active Call in Progress'
    bannerClass = 'bg-gradient-to-r from-primary to-primary-container text-on-primary'
    iconName = 'call'
  }

  return (
    <div className="fixed inset-y-0 right-0 w-[420px] bg-surface-container-lowest shadow-2xl border-l border-outline-variant/30 flex flex-col z-[9999] animate-slide-in-right">
      {/* Top Banner & Header */}
      <div className={`${bannerClass} p-5 flex items-center justify-between shadow-sm transition-colors duration-300`}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
            <span className="material-symbols-outlined text-xl">{iconName}</span>
          </div>
          <div>
            <h3 className="font-display font-bold text-base">
              {titleText}
            </h3>
            <p className="text-xs text-blue-100">{effectiveNumber}</p>
          </div>
        </div>

        {/* Minimize to Header Button */}
        {callState !== 'RINGING' && callState !== 'MISSED' && (
          <button
            type="button"
            onClick={handleMinimize}
            title="Minimize to Header Dock Bar"
            className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-xs font-semibold flex items-center gap-1 transition-colors"
          >
            <span className="material-symbols-outlined text-base">close_fullscreen</span>
            <span>Minimize</span>
          </button>
        )}
      </div>

      {/* Drawer Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-5 space-y-4 font-body">
        {/* Customer & Project Context Card */}
        <div className="p-4 rounded-xl bg-surface-container-low border border-outline-variant/20 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider text-primary">
              Matched Customer Context
            </span>
            {isKnownCaller && (
              <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-800 text-[10px] font-bold flex items-center gap-1">
                <span className="material-symbols-outlined text-xs">verified</span>
                <span>Verified Contact</span>
              </span>
            )}
          </div>
          <h4 className="text-base font-bold text-on-surface font-display">{effectiveContactName}</h4>
          <p className="text-xs font-medium text-on-surface-variant flex items-center gap-1">
            <span className="material-symbols-outlined text-sm text-primary">deployed_code</span>
            <span>Project: {effectiveProjectName || 'General / Pre-sales Inquiry'}</span>
          </p>
          <p className="text-xs text-on-surface-variant/80 flex items-center gap-1">
            <span className="material-symbols-outlined text-sm text-secondary">person</span>
            <span>Contact: {effectiveContactPerson || 'Caller: ' + effectiveNumber}</span>
          </p>
        </div>

        {/* Last Interaction & Open Items Card */}
        <div className="p-4 rounded-xl bg-surface-container-lowest border border-outline-variant/30 space-y-3">
          <h5 className="text-xs font-bold text-on-surface uppercase tracking-wider">
            Last Interaction Summary
          </h5>
          <p className="text-xs text-on-surface-variant leading-relaxed">{lastInteraction}</p>
          
          <div className="flex items-center gap-2 pt-1">
            <span className="px-2.5 py-1 rounded-md bg-blue-50 text-blue-700 text-xs font-medium">
              {openTicketsCount} Open Tickets
            </span>
            <span className="px-2.5 py-1 rounded-md bg-purple-50 text-purple-700 text-xs font-medium">
              {openTasksCount} Open Tasks
            </span>
          </div>
        </div>

        {/* Quick Actions Panel */}
        <div className="space-y-2">
          <span className="text-xs font-bold text-on-surface uppercase tracking-wider">
            Quick Actions
          </span>
          <div className="grid grid-cols-3 gap-2">
            <button
              type="button"
              onClick={onQuickCreateTask}
              className="p-2.5 rounded-lg border border-outline-variant/30 bg-surface-container-lowest hover:bg-surface-container-low text-xs font-medium text-on-surface flex flex-col items-center gap-1 transition-colors"
            >
              <span className="material-symbols-outlined text-primary">add_task</span>
              <span>+ Task</span>
            </button>

            <button
              type="button"
              onClick={onQuickCreateJob}
              className="p-2.5 rounded-lg border border-outline-variant/30 bg-surface-container-lowest hover:bg-surface-container-low text-xs font-medium text-on-surface flex flex-col items-center gap-1 transition-colors"
            >
              <span className="material-symbols-outlined text-secondary">work</span>
              <span>+ Job</span>
            </button>

            <button
              type="button"
              onClick={onScheduleCallback}
              className="p-2.5 rounded-lg border border-outline-variant/30 bg-surface-container-lowest hover:bg-surface-container-low text-xs font-medium text-on-surface flex flex-col items-center gap-1 transition-colors"
            >
              <span className="material-symbols-outlined text-tertiary">edit_calendar</span>
              <span>Callback</span>
            </button>
          </div>
        </div>

        {/* Live Call Notes Text Area */}
        <div className="space-y-2 pt-1">
          <label className="text-xs font-bold text-on-surface uppercase tracking-wider block">
            Call Notes (Auto-saves to Timesheet)
          </label>
          <textarea
            value={notesText}
            onChange={(e) => {
              setNotesText(e.target.value)
              onSaveNotes(e.target.value)
            }}
            rows={4}
            placeholder="Type call notes, discussion summary, or customer requests here..."
            className="w-full p-3 rounded-xl border border-outline-variant/40 bg-surface-container-lowest text-xs text-on-surface focus:ring-2 focus:ring-primary focus:outline-none transition-all"
          />
        </div>
      </div>

      {/* Bottom Action Footer */}
      <div className="p-4 bg-surface-container-low border-t border-outline-variant/30 flex flex-col gap-3">
        {callState === 'MISSED' ? (
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleDecline}
              className="flex-1 py-3 rounded-xl bg-surface-container-highest hover:bg-surface-container-high text-on-surface font-bold text-sm flex items-center justify-center gap-2 shadow-sm transition-colors"
            >
              <span className="material-symbols-outlined text-lg">close</span>
              <span>Dismiss Notification</span>
            </button>
          </div>
        ) : callState === 'CONNECTING' ? (
          <div className="flex items-center justify-center p-3 text-sm font-semibold text-primary gap-2">
            <span className="material-symbols-outlined animate-spin text-xl">progress_activity</span>
            <span>Connecting audio stream...</span>
          </div>
        ) : callState === 'RINGING' ? (
          <div className="flex items-center gap-3">
            {effectiveDirection === 'inbound' && (
              <button
                type="button"
                onClick={handleAnswer}
                className="flex-1 py-3.5 rounded-xl bg-green-600 hover:bg-green-700 text-white font-bold text-sm flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-all active:scale-[0.98]"
              >
                <span className="material-symbols-outlined text-xl animate-bounce">call</span>
                <span>Answer Call</span>
              </button>
            )}
            <button
              type="button"
              onClick={handleDecline}
              className="flex-1 py-3.5 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold text-sm flex items-center justify-center gap-2 shadow-md transition-all active:scale-[0.98]"
            >
              <span className="material-symbols-outlined text-xl">call_end</span>
              <span>{effectiveDirection === 'outbound' ? 'Cancel Call' : 'Decline'}</span>
            </button>
          </div>
        ) : (

          <div className="space-y-3">
            {/* Active In-Call Action Control Bar */}
            <div className="flex items-center justify-around p-2.5 rounded-xl bg-surface-container-lowest border border-outline-variant/30 shadow-2xs">
              <button
                type="button"
                onClick={onToggleMute}
                title={isMuted ? 'Unmute' : 'Mute'}
                className={`p-2.5 rounded-full text-on-surface-variant hover:bg-surface-container-highest transition-colors ${
                  isMuted ? 'text-amber-600 bg-amber-50 font-bold' : ''
                }`}
              >
                <span className="material-symbols-outlined text-xl">
                  {isMuted ? 'mic_off' : 'mic'}
                </span>
              </button>

              <button
                type="button"
                onClick={onToggleHold}
                title={isOnHold ? 'Unhold' : 'Hold'}
                className={`p-2.5 rounded-full text-on-surface-variant hover:bg-surface-container-highest transition-colors ${
                  isOnHold ? 'text-amber-600 bg-amber-50 font-bold' : ''
                }`}
              >
                <span className="material-symbols-outlined text-xl">
                  {isOnHold ? 'play_arrow' : 'pause'}
                </span>
              </button>

              <button
                type="button"
                title="Transfer Call"
                className="p-2.5 rounded-full text-on-surface-variant hover:bg-surface-container-highest transition-colors"
              >
                <span className="material-symbols-outlined text-xl">phone_forwarded</span>
              </button>
            </div>

            <button
              type="button"
              onClick={() => {
                onSaveNotes(notesText)
                handleDecline()
              }}
              className="w-full py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold text-sm flex items-center justify-center gap-2 shadow-md transition-colors"
            >
              <span className="material-symbols-outlined text-lg">call_end</span>
              <span>End Call & Save Timesheet</span>
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
